import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { fetchWithAuth } from '../utils/api';
import '../styles/GitHubRepoViewer.css';

function GitHubRepoViewer({ owner, repo, branch, onFileSelect }) {
  const [currentPath, setCurrentPath] = useState('');
  const [contents, setContents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  const fetchContents = useCallback(async (path = '') => {
    setLoading(true);
    setError(null);
    setFileContent(null);

    try {
      const params = new URLSearchParams();
      if (path) params.append('path', path);
      if (branch) params.append('ref', branch);

      const url = `${API_BASE_URL}/github/repos/${owner}/${repo}/contents${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch repository contents');
      }

      const data = await response.json();

      if (data.type === 'file') {
        setFileContent(data);
        setContents(null);
      } else {
        setContents(data);
        setFileContent(null);
      }

      // Update breadcrumbs
      const parts = path ? path.split('/') : [];
      setBreadcrumbs(parts.map((part, index) => ({
        name: part,
        path: parts.slice(0, index + 1).join('/')
      })));
      setCurrentPath(path);
    } catch (err) {
      console.error('Error fetching contents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [owner, repo, branch]);

  useEffect(() => {
    if (owner && repo) {
      fetchContents('');
    }
  }, [owner, repo, fetchContents]);

  const handleItemClick = (item) => {
    if (item.type === 'dir' || item.type === 'tree') {
      fetchContents(item.path);
    } else if (item.type === 'file' || item.type === 'blob') {
      fetchContents(item.path);
      if (onFileSelect) {
        onFileSelect(item);
      }
    }
  };

  const navigateToBreadcrumb = (path) => {
    fetchContents(path);
  };

  const getFileIcon = (item) => {
    if (item.type === 'dir' || item.type === 'tree') {
      return '📁';
    }

    const ext = item.name.split('.').pop().toLowerCase();
    const iconMap = {
      'js': '📜',
      'jsx': '⚛️',
      'ts': '📘',
      'tsx': '⚛️',
      'json': '📋',
      'md': '📝',
      'css': '🎨',
      'html': '🌐',
      'py': '🐍',
      'rb': '💎',
      'go': '🔷',
      'rs': '🦀',
      'java': '☕',
      'sql': '🗃️',
      'yml': '⚙️',
      'yaml': '⚙️',
      'sh': '💻',
      'env': '🔐',
      'lock': '🔒',
    };

    return iconMap[ext] || '📄';
  };

  const formatFileSize = (size) => {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getLanguage = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const langMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'json': 'json',
      'md': 'markdown',
      'css': 'css',
      'html': 'html',
      'py': 'python',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'java': 'java',
      'sql': 'sql',
      'yml': 'yaml',
      'yaml': 'yaml',
      'sh': 'bash',
    };
    return langMap[ext] || 'text';
  };

  if (loading && !contents && !fileContent) {
    return (
      <div className="github-repo-viewer loading">
        <div className="loading-spinner"></div>
        <p>Loading repository...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="github-repo-viewer error">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={() => fetchContents(currentPath)}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="github-repo-viewer">
      {/* Header with repo info and branch */}
      <div className="repo-header">
        <div className="repo-info">
          <span className="repo-icon">📦</span>
          <span className="repo-name">{owner}/{repo}</span>
          {branch && (
            <span className="branch-badge">
              <span className="branch-icon">🌿</span>
              {branch}
            </span>
          )}
        </div>
      </div>

      {/* Breadcrumb navigation */}
      <div className="breadcrumbs">
        <button
          className={`breadcrumb-item ${currentPath === '' ? 'active' : ''}`}
          onClick={() => navigateToBreadcrumb('')}
        >
          <span className="home-icon">🏠</span>
          root
        </button>
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.path}>
            <span className="breadcrumb-separator">/</span>
            <button
              className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
              onClick={() => navigateToBreadcrumb(crumb.path)}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Directory contents */}
      {contents && contents.type === 'directory' && (
        <div className="directory-contents">
          {loading && (
            <div className="loading-overlay">
              <div className="loading-spinner small"></div>
            </div>
          )}
          <table className="file-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Size</th>
              </tr>
            </thead>
            <tbody>
              {/* Sort: directories first, then files, alphabetically */}
              {[...contents.items]
                .sort((a, b) => {
                  const aIsDir = a.type === 'dir' || a.type === 'tree';
                  const bIsDir = b.type === 'dir' || b.type === 'tree';
                  if (aIsDir && !bIsDir) return -1;
                  if (!aIsDir && bIsDir) return 1;
                  return a.name.localeCompare(b.name);
                })
                .map((item) => (
                  <tr
                    key={item.path}
                    className={`file-row ${item.type === 'dir' || item.type === 'tree' ? 'directory' : 'file'}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <td className="file-name">
                      <span className="file-icon">{getFileIcon(item)}</span>
                      {item.name}
                    </td>
                    <td className="file-size">
                      {item.type === 'file' || item.type === 'blob' ? formatFileSize(item.size) : '-'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* File content viewer */}
      {fileContent && (
        <div className="file-viewer">
          <div className="file-header">
            <div className="file-info">
              <span className="file-icon">{getFileIcon({ name: fileContent.name, type: 'file' })}</span>
              <span className="file-name">{fileContent.name}</span>
              <span className="file-size">{formatFileSize(fileContent.size)}</span>
            </div>
            <div className="file-actions">
              <button
                className="back-btn"
                onClick={() => {
                  const parentPath = currentPath.split('/').slice(0, -1).join('/');
                  fetchContents(parentPath);
                }}
              >
                ← Back
              </button>
              {onFileSelect && (
                <button
                  className="select-file-btn"
                  onClick={() => onFileSelect(fileContent)}
                >
                  Select File
                </button>
              )}
            </div>
          </div>
          <div className="file-content">
            <pre className={`language-${getLanguage(fileContent.name)}`}>
              <code>{fileContent.content}</code>
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default GitHubRepoViewer;
